import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/admin/AdminLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { Plus, Edit, Trash2, Upload } from 'lucide-react';
import {
  getProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  uploadImage,
  uploadVideo,
} from '../api';
import { StreamingTab, HeroTab, SectionsTab } from '../components/admin/AdminTabs';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const { toast } = useToast();

  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <DashboardTab />;
      case 'products':
        return <ProductsTab toast={toast} />;
      case 'streaming':
        return <StreamingTab toast={toast} />;
      case 'hero':
        return <HeroTab toast={toast} />;
      case 'sections':
        return <SectionsTab toast={toast} />;
      default:
        return <DashboardTab />;
    }
  };

  return (
    <AdminLayout activeTab={activeTab} setActiveTab={setActiveTab}>
      {renderTabContent()}
    </AdminLayout>
  );
};

// Dashboard Tab
const DashboardTab = () => {
  return (
    <div>
      <h2 className="text-3xl font-bold mb-6">Dashboard</h2>
      <div className="grid md:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Total Products</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold text-orange-500">0</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Streaming Content</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold text-orange-500">0</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Active Sections</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold text-orange-500">4</div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Products Tab
const ProductsTab = ({ toast }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    time: '',
    location: '',
    venue: '',
    description: '',
    price: '',
    status: 'available',
    external_link: '',
    category: 'tour',
    images: [],
    videos: [],
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const data = await getProducts();
      setProducts(data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Gagal mengambil data products',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const result = await uploadImage(file);
      setFormData({
        ...formData,
        images: [...formData.images, result.url],
      });
      toast({
        title: 'Berhasil',
        description: 'Gambar berhasil diupload',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Gagal upload gambar',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const result = await uploadVideo(file);
      setFormData({
        ...formData,
        videos: [...formData.videos, result.url],
      });
      toast({
        title: 'Berhasil',
        description: 'Video berhasil diupload',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Gagal upload video',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProduct) {
        await updateProduct(editingProduct.id, formData);
        toast({
          title: 'Berhasil',
          description: 'Product berhasil diupdate',
        });
      } else {
        await createProduct(formData);
        toast({
          title: 'Berhasil',
          description: 'Product berhasil dibuat',
        });
      }
      setShowForm(false);
      setEditingProduct(null);
      setFormData({
        title: '',
        date: '',
        time: '',
        location: '',
        venue: '',
        description: '',
        price: '',
        status: 'available',
        external_link: '',
        category: 'tour',
        images: [],
        videos: [],
      });
      fetchProducts();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Gagal menyimpan product',
        variant: 'destructive',
      });
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData(product);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Yakin ingin menghapus product ini?')) return;

    try {
      await deleteProduct(id);
      toast({
        title: 'Berhasil',
        description: 'Product berhasil dihapus',
      });
      fetchProducts();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Gagal menghapus product',
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (showForm) {
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-3xl font-bold">
            {editingProduct ? 'Edit Product' : 'Tambah Product Baru'}
          </h2>
          <Button
            onClick={() => {
              setShowForm(false);
              setEditingProduct(null);
            }}
            variant="outline"
          >
            Batal
          </Button>
        </div>

        <Card>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Judul</label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Tanggal</label>
                  <Input
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    placeholder="Jun 7th, 7:00PM"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Waktu</label>
                  <Input
                    value={formData.time}
                    onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                    placeholder="7:00 PM"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Lokasi</label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="Greenville, SC"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Venue</label>
                  <Input
                    value={formData.venue}
                    onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
                    placeholder="Peace Center"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Harga</label>
                  <Input
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="Rp 500.000"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="available">Available</option>
                    <option value="sold-out">Sold Out</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Link Eksternal</label>
                  <Input
                    value={formData.external_link}
                    onChange={(e) => setFormData({ ...formData, external_link: e.target.value })}
                    placeholder="https://toko.mpstore.co.id/..."
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Deskripsi</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Upload Gambar</label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={uploading}
                />
                {formData.images.length > 0 && (
                  <div className="mt-2 grid grid-cols-4 gap-2">
                    {formData.images.map((img, idx) => (
                      <div key={idx} className="relative">
                        <img src={img} alt={`Upload ${idx}`} className="w-full h-20 object-cover rounded" />
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({
                              ...formData,
                              images: formData.images.filter((_, i) => i !== idx),
                            });
                          }}
                          className="absolute top-0 right-0 bg-red-500 text-white p-1 rounded"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Upload Video (Optional)</label>
                <Input
                  type="file"
                  accept="video/*"
                  onChange={handleVideoUpload}
                  disabled={uploading}
                />
              </div>

              <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600">
                {editingProduct ? 'Update Product' : 'Tambah Product'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-3xl font-bold">Tour Dates / Products</h2>
        <Button
          onClick={() => setShowForm(true)}
          className="bg-orange-500 hover:bg-orange-600"
        >
          <Plus className="mr-2" size={20} />
          Tambah Product
        </Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {products.map((product) => (
          <Card key={product.id}>
            <CardHeader>
              <CardTitle className="text-lg">{product.title}</CardTitle>
              <CardDescription>{product.date} - {product.location}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 mb-4">
                <p className="text-sm text-gray-600">{product.venue}</p>
                <p className="text-lg font-bold text-orange-500">{product.price}</p>
                <span
                  className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                    product.status === 'sold-out'
                      ? 'bg-gray-200 text-gray-700'
                      : 'bg-green-100 text-green-700'
                  }`}
                >
                  {product.status === 'sold-out' ? 'SOLD OUT' : 'AVAILABLE'}
                </span>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => handleEdit(product)}
                  variant="outline"
                  size="sm"
                  className="flex-1"
                >
                  <Edit size={16} className="mr-1" />
                  Edit
                </Button>
                <Button
                  onClick={() => handleDelete(product.id)}
                  variant="destructive"
                  size="sm"
                  className="flex-1"
                >
                  <Trash2 size={16} className="mr-1" />
                  Hapus
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {products.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500">Belum ada product. Klik tombol "Tambah Product" untuk membuat product baru.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminDashboard;
